import React from 'react';
import { motion } from 'framer-motion';
import PosterBackdrop from './PosterBackdrop';
import { EASE } from './Reveal';

function renderTitle(title, highlight, highlightClass) {
  if (!highlight || typeof title !== 'string') return title;
  const i = title.toLowerCase().indexOf(String(highlight).toLowerCase());
  if (i < 0) return title;
  return (
    <>
      {title.slice(0, i)}
      <span className={highlightClass}>{title.slice(i, i + highlight.length)}</span>
      {title.slice(i + highlight.length)}
    </>
  );
}

/** Solid three-colour bar (blue · yellow · pink) along the bottom edge: the poster signature. */
export function PosterStripe({ className = 'absolute inset-x-0 bottom-0' }) {
  return (
    <div aria-hidden="true" className={`flex h-1.5 ${className}`}>
      <span className="flex-[3] bg-primary" />
      <span className="flex-1 bg-sun" />
      <span className="flex-[2] bg-pop" />
    </div>
  );
}

const rise = (delay) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay, ease: EASE },
});

/**
 * Animated hero band for the top of inner pages (full width; contains its own container).
 * `<PageHeader eyebrow="AI Tools" icon={Sparkles} title="Note to codes" highlight="codes" subtitle="…">{actions}</PageHeader>`
 * Optional: `variant="navy"` (default, solid navy) | `"light"`, `above` (node rendered above the eyebrow,
 * e.g. breadcrumbs), `aside` (node rendered on the right at lg+), `className`, `size="md"|"sm"`.
 */
export default function PageHeader({ eyebrow, icon: Icon, title, highlight, subtitle, children, variant = 'navy', above, aside, className = '', size = 'md' }) {
  const navy = variant !== 'light';
  const pad = size === 'sm' ? 'py-8 sm:py-10' : 'py-10 sm:py-14';
  return (
    <section className={`relative isolate overflow-hidden ${navy ? 'bg-navy-deep text-on-navy' : 'border-b border-line/15 bg-surface text-ink'} ${className}`}>
      <PosterBackdrop variant={navy ? 'navy' : 'light'} />
      <PosterStripe />
      <div className={`container-page relative ${pad}`}>
        <div className={`flex flex-col gap-6 ${aside ? 'lg:flex-row lg:items-end lg:justify-between' : ''}`}>
          <div className="min-w-0 max-w-3xl">
            {above && <motion.div {...rise(0)} className={`mb-4 text-sm ${navy ? 'text-on-navy/70' : 'text-muted'}`}>{above}</motion.div>}
            {(eyebrow || Icon) && (
              <motion.div
                {...rise(0.02)}
                className={`mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] ${navy ? 'bg-sun text-sun-ink' : 'border border-primary/20 bg-sky-soft text-primary'}`}
              >
                {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
                {eyebrow}
              </motion.div>
            )}
            <motion.h1 {...rise(0.08)} className={`text-3xl font-extrabold leading-[1.08] sm:text-4xl lg:text-5xl ${navy ? 'text-on-navy' : 'text-ink'}`}>
              {renderTitle(title, highlight, navy ? 'text-sun' : 'text-primary')}
            </motion.h1>
            {subtitle && (
              <motion.p {...rise(0.16)} className={`mt-4 max-w-2xl text-base leading-relaxed sm:text-lg ${navy ? 'text-on-navy/75' : 'text-muted'}`}>
                {subtitle}
              </motion.p>
            )}
            {children && (
              <motion.div {...rise(0.24)} className="mt-6 flex flex-wrap items-center gap-3">
                {children}
              </motion.div>
            )}
          </div>
          {aside && (
            <motion.div {...rise(0.2)} className="shrink-0">
              {aside}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
